import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Tabs,
  Tab,
  Collapse,
  Chip,
  Divider,
} from '@mui/material';
import { Visibility, ExpandLess, ExpandMore, BeachAccess } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import { useTranslation } from 'react-i18next';

import api from '../../../api/axios';
import ReservationFormDialog from '../../../components/ReservationFormDialog';

/* ───────────────── types ───────────────── */
export interface ReservaPendiente {
  id: number;
  idProfesor: number;
  idClienteUsr: number;
  nombreProf: string;
  nombreCliente: string;
  fecha: string; // YYYY-MM-DD
  horaInicio: string; // HH:mm[:ss]
  horaFin: string; // HH:mm[:ss]
}

interface Profesor {
  id: number;
  nombre: string;
}

/* Vacaciones (entidad backend) */
type Tramo = 'manana' | 'tarde' | 'completo';
interface VacacionProfesorRow {
  id: number;
  idProfesor: number;
  fecha: string; // YYYY-MM-DD
  tramo: Tramo;
  motivo?: string | null;
}

/* Estado por code, más estable que label */
const PENDIENTE_REAGENDAR_CODE = 'PENDING_RESCHEDULE';
const hhmm = (v?: string | null) => (v ? String(v).slice(0, 5) : '');

/* ──────────────────────────────────────────────────────────────
   TAB «Gestión Vacaciones»
   ────────────────────────────────────────────────────────────── */
type VacacionesGestionTabProps = {
  onOpenReserva: (id: number) => void;
  /** cuando cambie, recarga datos de esta pestaña */
  refreshFlag: number;
};

function VacacionesGestionTab({ onOpenReserva, refreshFlag }: VacacionesGestionTabProps) {
  const { t, i18n } = useTranslation<'translation'>();
  const [loading, setLoading] = useState(true);

  // Reservas PENDING_RESCHEDULE del próximo mes
  const [pendMes, setPendMes] = useState<ReservaPendiente[]>([]);
  // Profes con vacaciones en el mes + pendientes (intersección)
  const [profConVac, setProfConVac] = useState<
    Array<{ id: number; nombre: string; vacDesde: string; vacHasta: string }>
  >([]);

  // Expansiones
  const [openProfId, setOpenProfId] = useState<number | null>(null);
  const [openAlumnoId, setOpenAlumnoId] = useState<number | null>(null);

  // Rango de 1 mes
  const desde = dayjs().startOf('day');
  const hasta = dayjs().add(1, 'month').endOf('day');
  const desdeStr = desde.format('YYYY-MM-DD');
  const hastaStr = hasta.format('YYYY-MM-DD');

  const isInRange = (iso: string) =>
    dayjs(iso).isSame(desde, 'day') || dayjs(iso).isBetween(desde, hasta, 'day', '[]');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      /* 1) Traemos PENDING_RESCHEDULE en el próximo mes */
      const { data: pend } = await api.get<{ meta: any; items: ReservaPendiente[] }>('/reservas', {
        params: {
          estadoCode: PENDIENTE_REAGENDAR_CODE,
          desde: desdeStr,
          hasta: hastaStr,
        },
      });
      const pendMesArr = Array.isArray(pend?.items) ? pend.items : [];
      setPendMes(pendMesArr);

      /* 2) Traemos vacaciones de los años implicados y filtramos al rango */
      const yearA = desde.year();
      const yearB = hasta.year();
      const calls = [
        api.get<VacacionProfesorRow[]>('/vacaciones', {
          params: { year: yearA },
        }),
      ];
      if (yearB !== yearA) {
        calls.push(
          api.get<VacacionProfesorRow[]>('/vacaciones', {
            params: { year: yearB },
          }),
        );
      }
      const results = await Promise.all(calls);
      const todasVacs = results.flatMap((r) => (Array.isArray(r.data) ? r.data : []));
      const vacsEnMes = todasVacs.filter((v) => isInRange(v.fecha));

      /* 3) Intersección: profes que tienen vacaciones y también pendientes */
      const profsConPend = new Map<number, string>();
      pendMesArr.forEach((r) => {
        if (!profsConPend.has(r.idProfesor)) profsConPend.set(r.idProfesor, r.nombreProf);
      });

      const vacsByProf = new Map<number, VacacionProfesorRow[]>();
      vacsEnMes.forEach((v) => {
        if (!vacsByProf.has(v.idProfesor)) vacsByProf.set(v.idProfesor, []);
        vacsByProf.get(v.idProfesor)!.push(v);
      });

      const out: Array<{
        id: number;
        nombre: string;
        vacDesde: string;
        vacHasta: string;
      }> = [];
      for (const [profId, vacs] of vacsByProf.entries()) {
        if (!profsConPend.has(profId)) continue; // debe tener también pendientes
        const nombre = profsConPend.get(profId) ?? `#${profId}`;
        const fechas = vacs.map((v) => v.fecha).sort((a, b) => a.localeCompare(b));
        const vacDesde = fechas[0];
        const vacHasta = fechas[fechas.length - 1];
        out.push({ id: profId, nombre, vacDesde, vacHasta });
      }

      out.sort((a, b) => a.vacDesde.localeCompare(b.vacDesde));
      setProfConVac(out);
    } finally {
      setLoading(false);
    }
  }, [desdeStr, hastaStr]);

  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  // carga inicial + cada vez que cambie refreshFlag (p.ej. al guardar/cerrar el diálogo)
  useEffect(() => {
    void loadData();
  }, [loadData, refreshFlag]);

  // Helpers para jerarquía
  const alumnosDe = useCallback(
    (profId: number) => {
      const filas = pendMes.filter((r) => r.idProfesor === profId);
      const mapa = new Map<number, { id: number; nombre: string; count: number }>();
      filas.forEach((r) => {
        const k = r.idClienteUsr;
        if (!mapa.has(k)) mapa.set(k, { id: k, nombre: r.nombreCliente, count: 0 });
        mapa.get(k)!.count += 1;
      });
      return Array.from(mapa.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
    },
    [pendMes],
  );

  const reservasDe = useCallback(
    (profId: number, alumnoId: number) =>
      pendMes
        .filter((r) => r.idProfesor === profId && r.idClienteUsr === alumnoId)
        .sort((a, b) => `${a.fecha} ${a.horaInicio}`.localeCompare(`${b.fecha} ${b.horaInicio}`)),
    [pendMes],
  );

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('vac.tab.title', { defaultValue: 'Gestión Vacaciones' })}
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('vac.tab.subtitle', {
          defaultValue:
            'Profesores con vacaciones en el próximo mes que además tienen reservas pendientes de reagendar.',
        })}
      </Typography>

      <List sx={{ mt: 1 }}>
        {profConVac.map((p) => {
          const open = openProfId === p.id;
          const alumnos = open ? alumnosDe(p.id) : [];

          return (
            <React.Fragment key={p.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setOpenProfId(open ? null : p.id);
                    setOpenAlumnoId(null);
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <BeachAccess fontSize="small" />
                        <span>{p.nombre}</span>
                        {(() => {
                          const fromFmt = dayjs(p.vacDesde).format('L');
                          const toFmt = dayjs(p.vacHasta).format('L');
                          const label =
                            p.vacDesde === p.vacHasta
                              ? t('vac.prof.vacDay', {
                                  date: fromFmt,
                                  defaultValue: `Vacaciones ${fromFmt}`,
                                })
                              : t('vac.prof.vacRange', {
                                  from: fromFmt,
                                  to: toFmt,
                                  defaultValue: `Vacaciones del ${fromFmt} al ${toFmt}`,
                                });
                          return <Chip size="small" label={label} />;
                        })()}
                      </Box>
                    }
                    secondary={t('vac.prof.hint', {
                      defaultValue: 'Click para ver alumnos con pendientes',
                    })}
                  />
                  {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>

              <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 2 }}>
                  {alumnos.map((a) => {
                    const openAlu = openAlumnoId === a.id;
                    return (
                      <React.Fragment key={a.id}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => setOpenAlumnoId(openAlu ? null : a.id)}>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <strong>{a.nombre}</strong>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={t('vac.student.count', {
                                      defaultValue: '{{count}} pendientes',
                                      count: a.count,
                                    })}
                                  />
                                </Box>
                              }
                              secondary={t('vac.student.hint', {
                                defaultValue: 'Click para ver reservas',
                              })}
                            />
                            {openAlu ? <ExpandLess /> : <ExpandMore />}
                          </ListItemButton>
                        </ListItem>

                        <Collapse in={openAlu} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding sx={{ pl: 4 }}>
                            {reservasDe(p.id, a.id).map((r) => {
                              const dateStr = dayjs(r.fecha).format('L');
                              const ini = hhmm(r.horaInicio);
                              const fin = hhmm(r.horaFin);

                              return (
                                <ListItem
                                  key={r.id}
                                  divider
                                  secondaryAction={
                                    <IconButton
                                      edge="end"
                                      aria-label="view"
                                      onClick={() => onOpenReserva(r.id)}
                                    >
                                      <Visibility />
                                    </IconButton>
                                  }
                                >
                                  <ListItemText
                                    primary={t('pending.list.item', {
                                      id: r.id,
                                    })}
                                    secondary={t('pending.list.secondary', {
                                      teacher: r.nombreProf,
                                      student: r.nombreCliente,
                                      date: dateStr,
                                      start: ini || '—',
                                      end: fin || '—',
                                    })}
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}

                  {alumnos.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      {t('vac.students.empty', {
                        defaultValue: 'Sin alumnos con pendientes.',
                      })}
                    </Typography>
                  )}
                </List>
                <Divider sx={{ my: 1 }} />
              </Collapse>
            </React.Fragment>
          );
        })}

        {profConVac.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {t('vac.prof.empty', {
              defaultValue:
                'No hay profesores con vacaciones próximas y reservas pendientes de reagendar.',
            })}
          </Typography>
        )}
      </List>
    </Box>
  );
}

/* ──────────────────────────────────────────────────────────────
   Página con pestañas (Pendientes | Gestión Vacaciones)
   ────────────────────────────────────────────────────────────── */
export default function ReservasPendientesPage() {
  const { t, i18n } = useTranslation<'translation'>();

  /* sincroniza locale de Day.js con i18next */
  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  /* ────────── estado base ────────── */
  const [reservas, setReservas] = useState<ReservaPendiente[]>([]);
  const [loading, setLoading] = useState(true);

  /* filtros */
  const [profFiltro, setProfFiltro] = useState<string>('');
  const [aluFiltro, setAluFiltro] = useState<string>('');

  /* ────────── diálogo edición ────────── */
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgData, setDlgData] = useState<any>(null);
  const [dlgId, setDlgId] = useState<number | null>(null);

  /* ────────── pestañas ────────── */
  const [tab, setTab] = useState<number>(0);

  /* ────────── trigger de refresco para la pestaña Vacaciones ────────── */
  const [vacRefresh, setVacRefresh] = useState(0);

  /* ────────── fetch reservas (pestaña pendientes) ────────── */
  const fetchReservas = useCallback(async () => {
    setLoading(true);
    try {
      const today = dayjs().startOf('day');
      const six = today.add(6, 'month');

      const { data } = await api.get<{ meta: any; items: ReservaPendiente[] }>('/reservas', {
        params: {
          estadoCode: PENDIENTE_REAGENDAR_CODE, // ← usamos CODE, no label
          desde: today.format('YYYY-MM-DD'),
          hasta: six.format('YYYY-MM-DD'),
        },
      });
      setReservas(Array.isArray(data?.items) ? data.items : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchReservas();
  }, [fetchReservas]);

  /* ────────── opciones dependientes ────────── */
  const profesoresOpts: Profesor[] = useMemo(() => {
    let base = reservas;
    if (aluFiltro) base = base.filter((r) => r.nombreCliente === aluFiltro);
    const m = new Map<number, string>();
    base.forEach((r) => {
      if (!m.has(r.idProfesor)) m.set(r.idProfesor, r.nombreProf);
    });
    return Array.from(m, ([id, nombre]) => ({ id, nombre }));
  }, [reservas, aluFiltro]);

  const alumnosOpts: string[] = useMemo(() => {
    let base = reservas;
    if (profFiltro) base = base.filter((r) => r.idProfesor === Number(profFiltro));
    return Array.from(new Set(base.map((r) => r.nombreCliente))).sort((a, b) => a.localeCompare(b));
  }, [reservas, profFiltro]);

  /* reset filtros inválidos */
  useEffect(() => {
    if (profFiltro && !profesoresOpts.some((p) => p.id === Number(profFiltro))) {
      setProfFiltro('');
    }
  }, [profesoresOpts, profFiltro]);

  useEffect(() => {
    if (aluFiltro && !alumnosOpts.includes(aluFiltro)) {
      setAluFiltro('');
    }
  }, [alumnosOpts, aluFiltro]);

  /* ────────── reservas filtradas ────────── */
  const reservasFiltradas = useMemo(
    () =>
      reservas.filter((r) => {
        const okProf = profFiltro ? r.idProfesor === Number(profFiltro) : true;
        const okAlu = aluFiltro ? r.nombreCliente === aluFiltro : true;
        return okProf && okAlu;
      }),
    [reservas, profFiltro, aluFiltro],
  );

  /* ────────── abrir diálogo ────────── */
  const handleOpenDialog = async (id: number) => {
    setDlgOpen(true);
    setDlgId(id);
    setDlgData(null);
    try {
      const { data } = await api.get(`/reservas/${id}`);
      setDlgData(data);
    } catch (err) {
      // Error cargando reserva
    }
  };

  const handleCloseDialog = () => {
    setDlgOpen(false);
    setDlgId(null);
    setDlgData(null);

    // Refresca ambas pestañas:
    void fetchReservas(); // pestaña Pendientes
    setVacRefresh((n) => n + 1); // pestaña Gestión Vacaciones
  };

  /* ────────── UI ────────── */
  const loadingCurrentTab = tab === 0 ? loading : false;

  if (loadingCurrentTab) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {tab === 0
          ? t('pending.title')
          : t('vac.tab.title', { defaultValue: 'Gestión Vacaciones' })}
      </Typography>

      {/* Pestañas */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2 }}
        aria-label="tabs-pendientes-vacaciones"
      >
        <Tab label={t('pending.tab.label', { defaultValue: 'Pendientes' })} />
        <Tab label={t('vac.tab.label', { defaultValue: 'Gestión Vacaciones' })} />
      </Tabs>

      {/* TAB 0: Pendientes (contenido actual) */}
      {tab === 0 && (
        <Box>
          {/* filtros */}
          <Box display="flex" gap={2} mb={3}>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="prof-label">{t('pending.filter.professor.label')}</InputLabel>
              <Select
                labelId="prof-label"
                value={profFiltro}
                label={t('pending.filter.professor.label')}
                onChange={(e: SelectChangeEvent<string>) => setProfFiltro(e.target.value)}
              >
                <MenuItem value="">{t('pending.filter.all')}</MenuItem>
                {profesoresOpts.map((p) => (
                  <MenuItem key={p.id} value={p.id.toString()}>
                    {p.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 220 }} size="small">
              <InputLabel id="alu-label">{t('pending.filter.student.label')}</InputLabel>
              <Select
                labelId="alu-label"
                value={aluFiltro}
                label={t('pending.filter.student.label')}
                onChange={(e: SelectChangeEvent<string>) => setAluFiltro(e.target.value)}
              >
                <MenuItem value="">{t('pending.filter.all')}</MenuItem>
                {alumnosOpts.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* lista */}
          <List>
            {reservasFiltradas.map((r) => {
              const dateStr = dayjs(r.fecha).format('L');
              const ini = hhmm(r.horaInicio);
              const fin = hhmm(r.horaFin);

              return (
                <ListItem
                  key={r.id}
                  divider
                  secondaryAction={
                    <IconButton edge="end" aria-label="view" onClick={() => handleOpenDialog(r.id)}>
                      <Visibility />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={t('pending.list.item', { id: r.id })}
                    secondary={t('pending.list.secondary', {
                      teacher: r.nombreProf,
                      student: r.nombreCliente,
                      date: dateStr,
                      start: ini || '—',
                      end: fin || '—',
                    })}
                  />
                </ListItem>
              );
            })}

            {reservasFiltradas.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {t('pending.list.empty')}
              </Typography>
            )}
          </List>
        </Box>
      )}

      {/* TAB 1: Gestión Vacaciones */}
      {tab === 1 && (
        <VacacionesGestionTab onOpenReserva={handleOpenDialog} refreshFlag={vacRefresh} />
      )}

      {/* diálogo edición */}
      {dlgOpen && (
        <ReservationFormDialog
          open={dlgOpen}
          mode="edit"
          reservaId={dlgId!}
          initialValues={dlgData}
          onClose={handleCloseDialog}
        />
      )}
    </Box>
  );
}
